import type { Caption } from '@remotion/captions';
import { createDownloadTracker, type ModelDownload } from './downloadProgress';

/**
 * GPU transcription via transformers.js → ONNX Runtime Web → WebGPU.
 *
 * This is a different runtime from @remotion/whisper-web (which is
 * whisper.cpp compiled to WASM and is CPU-only by construction — its
 * `transcribe()` exposes no device option at all).
 */

export type WebGpuModelId =
  | 'onnx-community/whisper-tiny_timestamped'
  | 'onnx-community/whisper-base_timestamped'
  | 'onnx-community/whisper-small_timestamped';

export const WEBGPU_MODELS: { id: WebGpuModelId; label: string; approxMb: number }[] = [
  { id: 'onnx-community/whisper-tiny_timestamped', label: 'tiny (fastest)', approxMb: 80 },
  { id: 'onnx-community/whisper-base_timestamped', label: 'base (balanced)', approxMb: 150 },
  { id: 'onnx-community/whisper-small_timestamped', label: 'small (most accurate)', approxMb: 490 },
];

type Chunk = { text: string; timestamp: [number, number | null] };
type AsrResult = { text: string; chunks?: Chunk[] };
// The pipeline's call signature is loosely typed upstream; narrow it here.
type AsrPipeline = (audio: Float32Array, opts: Record<string, unknown>) => Promise<AsrResult>;

let cached: { key: string; pipe: AsrPipeline } | null = null;

const expectedBytes = (model: WebGpuModelId): number =>
  (WEBGPU_MODELS.find((m) => m.id === model)?.approxMb ?? 0) * 1_048_576;

const getPipeline = async (
  model: WebGpuModelId,
  onProgress: (d: ModelDownload) => void,
): Promise<AsrPipeline> => {
  const key = `webgpu:${model}`;
  if (cached?.key === key) return cached.pipe;

  // Imported on demand: transformers.js is ~500 kB and the WASM engine never
  // needs it, so it must not sit in the entry chunk.
  const { pipeline, env } = await import('@huggingface/transformers');

  // Models come from the Hub; there is no local copy to fall back to.
  env.allowLocalModels = false;

  // Opt-in only, and it must agree with scripts/copy-ort-wasm.mjs: pointing
  // wasmPaths at an origin that lacks the *complete* backend set fails with
  // "Failed to fetch dynamically imported module". ORT picks between its
  // plain/jsep/asyncify/jspi builds at runtime and fetches a .mjs loader
  // alongside each .wasm, so a partial copy is worse than none.
  // Unset, ORT loads from its own CDN, which sends CORP and works under COEP.
  // BASE_URL keeps this correct under a sub-path deploy.
  if (env.backends.onnx.wasm && import.meta.env.VITE_ORT_SELF_HOST === 'true') {
    env.backends.onnx.wasm.wasmPaths = `${import.meta.env.BASE_URL}ort/`;
  }

  // A model is a dozen separate files; the tracker turns their interleaved,
  // per-file events into one number that only ever goes up.
  const tracker = createDownloadTracker(expectedBytes(model));

  const pipe = (await pipeline('automatic-speech-recognition', model, {
    device: 'webgpu',
    // The encoder is kept at fp32 deliberately: fp16 encoders currently hit
    // precision bugs on the WebGPU backend that surface as garbled or empty
    // transcripts. Only the decoder is quantised.
    // https://github.com/huggingface/transformers.js/issues/1590
    dtype: { encoder_model: 'fp32', decoder_model_merged: 'q4' },
    progress_callback: (p) => onProgress(tracker.push(p)),
  })) as unknown as AsrPipeline;

  cached = { key, pipe };
  return pipe;
};

const toCaptions = (chunks: Chunk[], fallbackEndMs: number): Caption[] =>
  chunks
    .map((chunk, i) => {
      const startMs = Math.round((chunk.timestamp[0] ?? 0) * 1000);
      // Whisper emits a null end timestamp for the final word often enough
      // that it must be handled, or the last caption never renders.
      const rawEnd = chunk.timestamp[1];
      const nextStart = chunks[i + 1]?.timestamp[0];
      const endMs =
        rawEnd != null
          ? Math.round(rawEnd * 1000)
          : nextStart != null
            ? Math.round(nextStart * 1000)
            : fallbackEndMs;

      return {
        text: chunk.text,
        startMs,
        endMs: Math.max(endMs, startMs + 40),
        timestampMs: Math.round((startMs + endMs) / 2),
        confidence: null,
      };
    })
    .filter((c) => c.text.trim().length > 0);

export const transcribeOnWebGpu = async (opts: {
  waveform: Float32Array;
  model: WebGpuModelId;
  language: string;
  onModelProgress: (d: ModelDownload) => void;
  /** Fires once weights are resident and inference is about to start. */
  onLoaded: () => void;
  onProgress: (p: number) => void;
}): Promise<Caption[]> => {
  const pipe = await getPipeline(opts.model, opts.onModelProgress);
  opts.onLoaded();

  const durationSec = opts.waveform.length / 16_000;

  // Whisper's window is 30s. Chunking the *timestamped* models has an open bug
  // at exactly chunk_length_s: 30, so only chunk when the clip actually needs
  // it and stay under the boundary when we do.
  // https://github.com/huggingface/transformers.js/issues/1358
  const needsChunking = durationSec > 28;

  opts.onProgress(0.05);
  const result = await pipe(opts.waveform, {
    return_timestamps: 'word',
    language: opts.language === 'auto' ? undefined : opts.language,
    ...(needsChunking ? { chunk_length_s: 25, stride_length_s: 5 } : {}),
  });
  opts.onProgress(1);

  if (!result.chunks || result.chunks.length === 0) {
    throw new Error(
      'The GPU engine returned no word timestamps. The model may not be a *_timestamped build.',
    );
  }

  return toCaptions(result.chunks, Math.round(durationSec * 1000));
};
